import type {
  Granularity,
  MonthlyStatementType,
  FeeCalculationType,
  MixedProductType,
  ProductType,
  TransactionStatus,
  TransactionType,
} from 'src/apis/capacity-management/types';
import { labelsToOptions } from 'src/utils/labels-to-options';
import { labelsToValues } from 'src/utils/labels-to-values';

export const granularityLabels: Record<Granularity, string> = {
  DAILY: 'Daily',
  HOURLY: 'Hourly',
};

export const granularityOptions = labelsToOptions(granularityLabels);
export const granularityValues = labelsToValues(granularityLabels);

export const monthlyStatementTypeLabels: Record<MonthlyStatementType, string> =
  {
    CAPACITY_FEE: 'Capacity Fee',
    COMMODITY_FEE: 'Commodity Fee',
  };

export const monthlyStatementOptions = labelsToOptions(
  monthlyStatementTypeLabels
);

const feeCalcationTypeLabels: Record<FeeCalculationType, string> = {
  HOUR_BASED: 'Hour Based Calculation',
  MONTH_BASED: 'Month Based Calculation (Flat Fee)',
};

export const feeCalculationTypeOptions = labelsToOptions(
  feeCalcationTypeLabels
);

export const feeCalculationTypeValues = labelsToValues(feeCalcationTypeLabels);

const productTypeLabels: Record<ProductType, string> = {
  WITHINDAY: 'Within-Day',
  DAY: 'Day',
  MONTH: 'Month',
  QUARTER: 'Quarter',
  YEAR: 'Year',
};

export const mixedProductTypeLabels: Record<MixedProductType, string> = {
  ...productTypeLabels,
  CUSTOM: 'Custom',
};

export const mixedProductTypeShortLabels: Record<MixedProductType, string> = {
  WITHINDAY: 'W',
  DAY: 'D',
  MONTH: 'M',
  QUARTER: 'Q',
  YEAR: 'Y',
  CUSTOM: 'Custom',
};

export const mixedProductTypeOptions = labelsToOptions(mixedProductTypeLabels);
export const mixedProductTypeValues = labelsToValues(mixedProductTypeLabels);

// drafted products never have within-day runtimes
const { WITHINDAY, ...draftedProductTypeLabels } = productTypeLabels;

export { draftedProductTypeLabels };

export const draftedProductTypeValues = labelsToValues(
  draftedProductTypeLabels
);
export const draftedProductTypeOptions = labelsToOptions(
  draftedProductTypeLabels
);

export const transactionTypeLabels: Record<TransactionType, string> = {
  AUCTION: 'Auction',
  SURRENDER: 'Surrender',
  CONVERSION: 'Conversion',
  ASSIGNMENT_BUY: 'Assignment (Buy)',
  TRANSFER_OF_USE_BUY: 'Transfer of Use (Buy)',
  ASSIGNMENT_SELL: 'Assignment (Sell)',
  TRANSFER_OF_USE_SELL: 'Transfer of Use (Sell)',
  MULTI_YEAR_TRANSPORT_ALLOCATION: 'Multi-Year Transport Allocation',
  DIRECT_FCFS: 'FCFS',
  FCFS: 'FCFS',
  RESHUFFLE: 'Reshuffle (To)',
  RESHUFFLE_FROM: 'Reshuffle (From)',
};

export const transactionTypeValues = labelsToValues(transactionTypeLabels);

export const getTransactionTypeOptions = ({
  hasMultiYearTransactions,
  isV3,
  hasReshuffleTransactions = false,
}: {
  hasMultiYearTransactions: boolean;
  isV3: boolean;
  hasReshuffleTransactions?: boolean;
}) =>
  labelsToOptions(transactionTypeLabels).filter((option) => {
    // merging 'DIRECT_FCFS', 'FCFS' into one filter, should only show one FCFS option
    if (!isV3 && option.value === 'FCFS') return false;
    if (isV3 && option.value === 'DIRECT_FCFS') return false;
    if (
      !hasMultiYearTransactions &&
      option.value === 'MULTI_YEAR_TRANSPORT_ALLOCATION'
    )
      return false;
    if (
      !hasReshuffleTransactions &&
      (option.value === 'RESHUFFLE' || option.value === 'RESHUFFLE_FROM')
    )
      return false;
    return true;
  });

export const transactionStatusLabels: Record<TransactionStatus, string> = {
  PENDING: 'Pending',
  SUCCESSFUL: 'Successful',
  UNSUCCESSFUL: 'Unsuccessful',
  INVALIDATED: 'Invalidated',
};

export const transactionStatusOptions = labelsToOptions(
  transactionStatusLabels
);

export const transactionStatusValues = labelsToValues(transactionStatusLabels);
